import {useTheme} from '@mui/material'
import {getProductLineColors} from '@progynyglobal/pg-ui-library'

import {useConsultationsDetailQuery} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'

interface UseConsultationPageLoading {
  loading: true
  error: false
  consultation: null
  colors: null
  theme: null
  refetch: () => Promise<any>
}

interface UseConsultationPageError {
  loading: false
  error: true
  consultation: null
  colors: null
  theme: null
  refetch: () => Promise<any>
}

interface UseConsultationPageSuccess {
  loading: false
  error: false
  consultation: any
  colors: any
  theme: any
  refetch: () => Promise<any>
}

type UseConsultationPageResult =
  | UseConsultationPageLoading
  | UseConsultationPageError
  | UseConsultationPageSuccess

/**
 * Custom hook that encapsulates all common consultation page setup logic.
 * Handles data fetching, theming, and loading/error states.
 *
 * @param slug - The consultation slug to fetch data for
 * @returns Object containing consultation data, colors, theme, and status flags
 *
 * @example
 * const {consultation, colors, theme, loading, error} = useConsultationPage('fertility')
 * if (loading) return <Spinner />
 * if (error) return <Error />
 * return <div>{consultation.title}</div>
 */
export const useConsultationPage = (
  slug: string
): UseConsultationPageResult => {
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const theme = useTheme()

  const {data, loading, error, refetch} = useConsultationsDetailQuery({
    variables: {slug},
    fetchPolicy: 'cache-first',
  })

  const consultation = data?.consultations?.consultation

  if (loading && !consultation) {
    return {
      loading: true,
      error: false,
      consultation: null,
      colors: null,
      theme: null,
      refetch,
    }
  }

  if (error || !consultation) {
    return {
      loading: false,
      error: true,
      consultation: null,
      colors: null,
      theme: null,
      refetch,
    }
  }

  return {
    loading: false,
    error: false,
    consultation,
    colors,
    theme,
    refetch,
  }
}
